import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import {getActionButtonRoleProps} from 'lib/a11y/ButtonA11y';
import ReagentChip from 'samples/plate-maps/fill-plate-modal/ReagentChip';
import {ReagentAssignmentGroup, Source, TestOrderInfoById} from 'samples/plate-maps/PlateMapConstants';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY, GRAY_DARKER, WHITE} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {immutableMove} from 'util/ImmutableListUtil';

interface OrderableReagentListItemProps {
  isFirstReagent: boolean;
  isLastReagent: boolean;
  onMoveDown: () => void;
  onMoveUp: () => void;
  onRemove: () => void;
  reagent: Source;
  reagentGroupsById: {[id: string]: ReagentAssignmentGroup};
  reagentOrdinal: number | null;
  sourceDilutionId: string;
  testOrderInfoById: TestOrderInfoById;
  reagentError: JSX.Element;
}

export function OrderableReagentListItem({
  isFirstReagent,
  isLastReagent,
  onMoveDown,
  onMoveUp,
  onRemove,
  reagent,
  reagentGroupsById,
  reagentOrdinal,
  sourceDilutionId,
  testOrderInfoById,
  reagentError,
}: OrderableReagentListItemProps): JSX.Element {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className={css(styles.reagentOuterWrapper)}
      onMouseEnter={() => {
        setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
      {...getTestProps({component: 'OrderableReagentListItem', element: 'root', key: sourceDilutionId})}
      data-amp-safe={false}
    >
      <div className={css(styles.reagentInnerWrapper)}>
        <div className={css(styles.reagentChipWrapper)}>
          {reagentOrdinal && (
            <div
              className={css(styles.number)}
              {...getTestProps({
                component: 'OrderableReagentListItem',
                element: 'ordinal',
                key: sourceDilutionId,
              })}
              data-amp-safe={false}
            >
              {reagentOrdinal}
            </div>
          )}
          <ReagentChip
            onRemove={onRemove}
            reagent={reagent}
            reagentGroupsById={reagentGroupsById}
            testOrderInfoById={testOrderInfoById}
          />
          {reagentError}
        </div>
        {hovered && (
          <div className={css(styles.iconsWrapper)}>
            <BenchlingIcon
              aria-label="Move reagent up"
              aria-disabled={isFirstReagent}
              className={classNames(css(styles.icon), {[css(styles.disabled)]: isFirstReagent})}
              icon="chevron-up"
              {...getActionButtonRoleProps({
                onPress: () => {
                  if (!isFirstReagent) {
                    onMoveUp();
                  }
                },
              })}
            />
            <BenchlingIcon
              aria-label="Move reagent down"
              aria-disabled={isLastReagent}
              className={classNames(css(styles.icon), {
                [css(styles.disabled)]: isLastReagent,
              })}
              icon="chevron-down"
              {...getActionButtonRoleProps({
                onPress: () => {
                  if (!isLastReagent) {
                    onMoveDown();
                  }
                },
              })}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export interface OrderableReagentListProps {
  onRemoveReagents: (sourceDilutionIds: Array<string>) => void;
  onReorderReagents: (sourceDilutionIdOrdering: Array<string>) => void;
  previewedReagentsBySourceDilutionId: {[id: string]: Source};
  previewedSourceDilutionIdOrdering: Array<string>;
  reagentGroupsById: {[id: string]: ReagentAssignmentGroup};
  orderedReagentLabels: Array<number> | null;
  testOrderInfoById: TestOrderInfoById;
  getErrorForSourceDilutionId: (sourceDilutionId: string) => JSX.Element;
}

export default function OrderableReagentList({
  onRemoveReagents,
  onReorderReagents,
  previewedReagentsBySourceDilutionId,
  previewedSourceDilutionIdOrdering,
  reagentGroupsById,
  orderedReagentLabels,
  testOrderInfoById,
  getErrorForSourceDilutionId,
}: OrderableReagentListProps): JSX.Element {
  return (
    <div
      className={css(styles.wrapper)}
      {...getTestProps({component: 'OrderableReagentList', element: 'root'})}
    >
      {previewedSourceDilutionIdOrdering.map((sourceDilutionId, index) => (
        <OrderableReagentListItem
          key={index}
          isFirstReagent={index === 0}
          isLastReagent={index === previewedSourceDilutionIdOrdering.length - 1}
          onMoveDown={() => {
            if (index !== previewedSourceDilutionIdOrdering.length - 1) {
              onReorderReagents(
                immutableMove(
                  previewedSourceDilutionIdOrdering,
                  previewedSourceDilutionIdOrdering[index],
                  index + 1
                )
              );
            }
          }}
          onMoveUp={() => {
            if (index !== 0) {
              onReorderReagents(
                immutableMove(
                  previewedSourceDilutionIdOrdering,
                  previewedSourceDilutionIdOrdering[index],
                  index - 1
                )
              );
            }
          }}
          onRemove={() => {
            onRemoveReagents([sourceDilutionId]);
          }}
          reagent={previewedReagentsBySourceDilutionId[sourceDilutionId]}
          reagentGroupsById={reagentGroupsById}
          reagentOrdinal={orderedReagentLabels == null ? index + 1 : (orderedReagentLabels[index] ?? null)}
          sourceDilutionId={sourceDilutionId}
          testOrderInfoById={testOrderInfoById}
          reagentError={getErrorForSourceDilutionId(sourceDilutionId)}
        />
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    minWidth: '300px',
  },
  reagentOuterWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '4px',
    ':hover': {
      backgroundColor: Color.GRAY_100,
    },
  },
  reagentInnerWrapper: {
    padding: '4px',
    flex: '1 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    maxWidth: '100%',
  },
  reagentChipWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  number: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    marginRight: '8px',
  },
  iconsWrapper: {
    border: `1px solid ${GRAY}`,
    borderRadius: '4px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: WHITE,
    color: GRAY_DARKER,
    padding: '4px',
    height: '24px',
    width: '48px',
  },
  icon: {
    height: '16px',
    width: '16px',
    cursor: 'pointer',
  },
  disabled: {
    color: GRAY,
    cursor: 'default',
  },
  button: {
    paddingLeft: '0px',
    paddingRight: '0px',
  },
});
